import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  ChartBarIcon,
  CogIcon,
  PowerIcon,
  RectangleStackIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

interface SubMenuItem {
  name: string;
  href: string;
}

interface MenuItem {
  name: string;
  href?: string;
  icon: any;
  subItems?: SubMenuItem[];
}

const Sidebar: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const [openMenus, setOpenMenus] = useState<string[]>(['Ingresos', 'Presupuesto']);

  const navigation: MenuItem[] = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Clientes', href: '/clientes', icon: UserGroupIcon },
    { name: 'Propuestas', href: '/propuestas', icon: DocumentChartBarIcon },
    { name: 'Contratos', href: '/contratos', icon: DocumentTextIcon },
    { 
      name: 'Ingresos', 
      icon: CurrencyDollarIcon,
      subItems: [
        { name: 'Ingresos Programados', href: '/pagos' },
        { name: 'Ingresos Reales', href: '/pagos-reales' },
        { name: 'Flujo de Caja', href: '/flujo-caja' },
      ]
    },
    { name: 'Gastos', href: '/gastos', icon: CurrencyDollarIcon },
    {
      name: 'Presupuesto',
      icon: ClipboardDocumentListIcon,
      subItems: [
        { name: 'Presupuesto Anual', href: '/presupuesto' },
        { name: 'Rubros Presupuestales', href: '/rubros-presupuestales' },
        { name: 'Análisis Presupuestal', href: '/analisis-presupuestal' },
      ]
    },
    { name: 'Conceptos', href: '/conceptos', icon: RectangleStackIcon },
    { name: 'Dashboard Financiero', href: '/dashboard-financiero', icon: ChartBarIcon },
    { name: 'Rentabilidad por Concepto', href: '/rentabilidad-conceptos', icon: ChartBarIcon },
    { name: 'Reportes', href: '/reportes', icon: ChartBarIcon },
  ];

  if (isAdmin) {
    navigation.push({ name: 'Configuración', href: '/config', icon: CogIcon });
  }

  const toggleMenu = (menuName: string) => {
    setOpenMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(m => m !== menuName)
        : [...prev, menuName]
    );
  };

  return (
    <div className="flex h-full w-64 flex-col bg-white border-r border-gray-200">
      <div className="flex flex-1 flex-col pt-5 pb-4 overflow-y-auto">
        <div className="flex flex-shrink-0 items-center px-4">
          <img
            className="h-8 w-auto"
            src="/logo512.png"
            alt="PuntoEdu CRM"
          />
          <span className="ml-3 text-lg font-semibold text-primary-500">
            PuntoEdu CRM
          </span>
        </div>
        <nav className="mt-8 flex-1 space-y-1 px-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isOpen = openMenus.includes(item.name);
            
            // Item con submenú
            if (item.subItems) {
              return (
                <div key={item.name}>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className="w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  >
                    <Icon className="mr-3 h-5 w-5 text-current" aria-hidden="true" />
                    <span className="flex-1 text-left">{item.name}</span>
                    {isOpen ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.subItems.map((subItem) => (
                        <NavLink
                          key={subItem.name}
                          to={subItem.href}
                          className={({ isActive }) =>
                            `${
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            } group flex items-center px-2 py-2 text-sm rounded-md`
                          }
                        >
                          {subItem.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            // Item sin submenú
            return (
              <NavLink
                key={item.name}
                to={item.href!}
                className={({ isActive }) =>
                  `${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border-primary-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md border-l-4 ${
                    isActive ? '' : 'border-transparent'
                  }`
                }
              >
                <Icon
                  className="mr-3 h-5 w-5 text-current"
                  aria-hidden="true"
                />
                {item.name}
              </NavLink>
            );
          })}
        </nav>
      </div>
      
      <div className="flex-shrink-0 border-t border-gray-200 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.nombre.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-700">{user?.nombre}</p>
            <p className="text-xs font-medium text-gray-500">{user?.role}</p>
          </div>
          <button
            onClick={logout}
            className="ml-auto p-1 rounded-full text-gray-400 hover:text-gray-500"
            title="Cerrar sesión"
          >
            <PowerIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;